import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Layers, Store } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { MARKETPLACE_LISTING_STATUS_LABELS } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate, num, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { Badge, Button, SearchSelect, statusColor } from "../components/ui";
import { buildCategorySelectOptions } from "../lib/categoryOptions";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { showQueryLoader } from "../lib/query-utils";
import { ImageHoverPreview } from "../components/ImageHoverPreview";
import { MarketplaceListingImpressionTrendPopover } from "../components/marketplace/MarketplaceListingImpressionTrendPopover";

type ListingStatus = "PENDING_REVIEW" | "ACTIVE" | "DELISTED" | "ALL";

interface ListingRow {
  id: string;
  title: string;
  slug: string;
  price: number;
  quantity: number;
  soldCount: number;
  avgRating: number;
  ratingCount: number;
  condition: string | null;
  images: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
  description?: string | null;
  categoryId?: string | null;
  categoryPath?: string | null;
  seller: { shopName: string; shopSlug: string };
  impressions7d?: { listing: number; detail: number; search: number };
}

const VIEW_OPTIONS: { value: ListingStatus; label: string }[] = [
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "ACTIVE", label: "Active" },
  { value: "DELISTED", label: "Delisted" },
  { value: "ALL", label: "All" },
];

const PAGE_SIZE = 25;

export function MarketplaceListings() {
  const qc = useQueryClient();
  const canModerate = useAuth((s) => s.can("marketplace.moderate_listings"));
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get("status") as ListingStatus | null) ?? "PENDING_REVIEW";
  const search = searchParams.get("search") ?? "";
  const [searchInput, setSearchInput] = useState(search);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const [trendListing, setTrendListing] = useState<{ id: string; title: string } | null>(null);
  const [panelCategoryId, setPanelCategoryId] = useState("");

  const categoriesQuery = useQuery({
    queryKey: ["catalog-categories"],
    queryFn: () =>
      api<{ categories: { id: string; name: string; slug: string; parentId: string | null }[] }>(
        "/catalog/categories",
      ),
    enabled: Boolean(selectedId) && canModerate,
  });

  const categoryOptions = useMemo(
    () => buildCategorySelectOptions(categoriesQuery.data?.categories ?? []),
    [categoriesQuery.data?.categories],
  );

  const listQuery = useInfiniteQuery({
    queryKey: ["marketplace-listings", view, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (view !== "ALL") params.set("status", view);
      if (search.trim()) params.set("q", search.trim());
      return api<{ items: ListingRow[]; total: number }>(`/marketplace/admin/listings?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.items.length, 0), PAGE_SIZE),
  });

  const rows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = rows.length < listTotal && (listQuery.hasNextPage ?? false);
  const selectedRow = rows.find((r) => r.id === selectedId) ?? null;

  useEffect(() => {
    setPanelCategoryId(selectedRow?.categoryId ?? "");
  }, [selectedRow?.id, selectedRow?.categoryId]);

  const bulkMut = useMutation({
    mutationFn: (action: "approve" | "reject") =>
      api("/marketplace/admin/listings/bulk", {
        method: "POST",
        body: { action, listingIds: [...selectedKeys] },
      }),
    onSuccess: (_, action) => {
      toast("success", action === "approve" ? "Listings approved" : "Listings rejected");
      setSelectedKeys(new Set());
      void qc.invalidateQueries({ queryKey: ["marketplace-listings"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Bulk action failed"),
  });

  const singleMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      api(`/marketplace/admin/listings/${id}/${action === "approve" ? "approve" : "reject"}`, { method: "POST" }),
    onSuccess: (_, { action }) => {
      toast("success", action === "approve" ? "Listing approved" : "Listing rejected");
      setSelectedId(null);
      void qc.invalidateQueries({ queryKey: ["marketplace-listings"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Action failed"),
  });

  const categoryMut = useMutation({
    mutationFn: ({ id, categoryId }: { id: string; categoryId: string }) =>
      api(`/marketplace/admin/listings/${id}`, { method: "PATCH", body: { categoryId } }),
    onSuccess: () => {
      toast("success", "Category updated");
      void qc.invalidateQueries({ queryKey: ["marketplace-listings"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Could not update category"),
  });

  const columns = useMemo<Column<ListingRow>[]>(
    () => [
      {
        key: "listing",
        header: "Listing",
        sortable: true,
        sortValue: (r) => r.title,
        render: (r) => {
          const thumb = r.images[0];
          return (
            <div className="flex items-center gap-2.5">
              {thumb ? (
                <ImageHoverPreview src={thumb} previewWidth={240} previewHeight={240}>
                  <img src={thumb} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-line object-cover" />
                </ImageHoverPreview>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-white/50">
                  <Layers size={16} />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate font-medium text-white/90">{r.title}</p>
                <p className="font-mono text-[10px] text-white/40">{r.slug}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: "shop",
        header: "Shop",
        hideSm: true,
        sortValue: (r) => r.seller.shopName,
        render: (r) => <span className="text-xs text-white/70">{r.seller.shopName}</span>,
      },
      {
        key: "condition",
        header: "Condition",
        hideSm: true,
        render: (r) => <span className="text-xs text-white/60">{r.condition ?? "—"}</span>,
      },
      {
        key: "impressions",
        header: "Impressions (7d)",
        className: "text-right",
        hideSm: true,
        sortValue: (r) => (r.impressions7d?.listing ?? 0) + (r.impressions7d?.detail ?? 0) + (r.impressions7d?.search ?? 0),
        render: (r) => {
          const imp = r.impressions7d;
          const total = (imp?.listing ?? 0) + (imp?.detail ?? 0) + (imp?.search ?? 0);
          return (
            <button
              type="button"
              className="text-right text-xs hover:text-brand-pink"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setTrendListing({ id: r.id, title: r.title });
              }}
            >
              <span className="font-medium tabular-nums">{num(total)}</span>
              <span className="block text-[10px] text-muted">
                {imp ? `${imp.listing} grid · ${imp.detail} detail` : "—"}
              </span>
            </button>
          );
        },
      },
      {
        key: "price",
        header: "Price",
        className: "text-right",
        sortValue: (r) => r.price,
        render: (r) => <span className="text-xs">{rm(r.price)}</span>,
      },
      {
        key: "sold",
        header: "Sold",
        className: "text-right",
        sortValue: (r) => r.soldCount,
        render: (r) => (
          <span className="text-xs">
            {num(r.soldCount)}/{num(r.quantity)}
          </span>
        ),
      },
      {
        key: "rating",
        header: "Rating",
        className: "text-right",
        hideSm: true,
        sortValue: (r) => r.avgRating,
        render: (r) => (
          <span className="text-xs tabular-nums">
            {r.ratingCount > 0 ? `${Number(r.avgRating).toFixed(1)} (${r.ratingCount})` : "—"}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortValue: (r) => r.status,
        render: (r) => (
          <Badge color={statusColor(r.status === "ACTIVE" ? "ACTIVE" : r.status === "PENDING_REVIEW" ? "PENDING" : "CANCELLED")}>
            {MARKETPLACE_LISTING_STATUS_LABELS[r.status] ?? r.status}
          </Badge>
        ),
      },
      {
        key: "updated",
        header: "Updated",
        hideSm: true,
        sortValue: (r) => r.updatedAt,
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.updatedAt)}</span>,
      },
    ],
    [],
  );

  const pendingSelected = [...selectedKeys].every((id) => rows.find((r) => r.id === id)?.status === "PENDING_REVIEW");

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={showQueryLoader(listQuery)}
        entityLabel="Listing"
        exportFilename="marketplace-listings"
        selectable={canModerate}
        selectedKeys={selectedKeys}
        onSelectedKeysChange={setSelectedKeys}
        onRowClick={(r) => setSelectedId(r.id)}
        activeKey={selectedId ?? undefined}
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        toolbar={{
          search: {
            value: searchInput,
            onChange: setSearchInput,
            onSubmit: () => {
              const next = new URLSearchParams(searchParams);
              if (searchInput.trim()) next.set("search", searchInput.trim());
              else next.delete("search");
              setSearchParams(next);
            },
            placeholder: "Search listings…",
          },
          views: {
            options: VIEW_OPTIONS,
            value: view,
            onChange: (v) => {
              const next = new URLSearchParams(searchParams);
              next.set("status", v);
              setSearchParams(next);
            },
          },
          onRefresh: () => void listQuery.refetch(),
        }}
        selection={
          canModerate && pendingSelected && selectedKeys.size > 0
            ? {
                trailing: (
                  <>
                    <Button variant="outline" size="sm" disabled={bulkMut.isPending} onClick={() => bulkMut.mutate("reject")}>
                      Reject
                    </Button>
                    <Button variant="primary" size="sm" disabled={bulkMut.isPending} onClick={() => bulkMut.mutate("approve")}>
                      Approve
                    </Button>
                  </>
                ),
              }
            : undefined
        }
        empty={{
          icon: Store,
          title: view === "PENDING_REVIEW" ? "No listings awaiting review" : "No listings",
          hint: "Seller submissions appear here when listing moderation is enabled.",
        }}
      />

      {trendListing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setTrendListing(null)}
        >
          <div
            className="rounded-2xl border border-line bg-surface p-4 shadow-xl"
            role="dialog"
            aria-label="Impression trend"
            onClick={(e) => e.stopPropagation()}
          >
            <MarketplaceListingImpressionTrendPopover listingId={trendListing.id} listingTitle={trendListing.title} />
            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setTrendListing(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <RightSidebarPanel
        open={!!selectedRow}
        onClose={() => setSelectedId(null)}
        title={selectedRow?.title ?? "Listing"}
        footer={
          selectedRow && canModerate ? (
            <div className="flex w-full flex-wrap justify-end gap-2">
              {panelCategoryId && panelCategoryId !== (selectedRow.categoryId ?? "") ? (
                <Button
                  variant="outline"
                  disabled={categoryMut.isPending}
                  onClick={() => categoryMut.mutate({ id: selectedRow.id, categoryId: panelCategoryId })}
                >
                  Save category
                </Button>
              ) : null}
              {selectedRow.status === "PENDING_REVIEW" ? (
                <>
                  <Button variant="outline" onClick={() => singleMut.mutate({ id: selectedRow.id, action: "reject" })}>
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!panelCategoryId && !selectedRow.categoryId}
                    onClick={() => singleMut.mutate({ id: selectedRow.id, action: "approve" })}
                  >
                    Approve
                  </Button>
                </>
              ) : null}
            </div>
          ) : null
        }
      >
        {selectedRow && (
          <div className="space-y-4 text-sm">
            <p className="text-muted">{selectedRow.seller.shopName}</p>
            {canModerate ? (
              <SearchSelect
                label="Category"
                placeholder="Select catalog category…"
                searchPlaceholder="Search categories…"
                options={categoryOptions}
                value={panelCategoryId}
                onChange={setPanelCategoryId}
                emptyMessage="No categories found"
                hint="Same category tree as shop products. Required before approve."
              />
            ) : selectedRow.categoryPath ? (
              <p className="text-xs text-muted">Category: {selectedRow.categoryPath}</p>
            ) : (
              <p className="text-xs text-amber-400/90">No category assigned</p>
            )}
            <p className="font-display text-lg text-brand-pink">{rm(selectedRow.price)}</p>
            <p className="text-muted">
              Sold {selectedRow.soldCount}/{selectedRow.quantity}
              {selectedRow.ratingCount > 0 ? ` · ${Number(selectedRow.avgRating).toFixed(1)}★ (${selectedRow.ratingCount})` : null}
            </p>
            {selectedRow.description ? (
              <p className="leading-relaxed text-foreground/90">{selectedRow.description}</p>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              {selectedRow.images.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt="" className="aspect-square w-full rounded-xl object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
